import React, { useMemo } from 'react'
import {
  EditButton,
  FilesListDisplay,
  RPCDocs,
  UserAnPCheck,
} from '@therms/react-modules'
import { Image, Modal } from '@therms/atalaya'
import { humanizeMinutes } from '@therms/web-js'
import dayjs from 'dayjs'

interface PassDownModalProps {
  closeHandler?: () => void
  onEditClick?: (passDownId: string) => void
  passDown:
    | RPCDocs.Locations.GetLocationPassDownsList.v1.PassDown
    | RPCDocs.PassDowns.GetPassDown.v1.PassDown
}

function PassDownModal({
  closeHandler,
  onEditClick,
  passDown,
}: PassDownModalProps) {
  const { active, activeDate, body, expireDate, id, primaryImageUrl, title } =
    passDown

  const statusMessage = useMemo(() => {
    let status
    let message
    if (active) {
      status = 'active'
      message = `Expires in ${humanizeMinutes(
        dayjs().diff(dayjs(expireDate), 'minute'),
      )}`
    } else if (dayjs().isBefore(dayjs(activeDate))) {
      status = 'future'
      message = `Activates in ${humanizeMinutes(
        dayjs().diff(dayjs(activeDate), 'minute'),
      )}`
    } else if (dayjs().isAfter(dayjs(expireDate))) {
      status = 'expired'
      message = `Expired ${humanizeMinutes(
        dayjs(expireDate).diff(dayjs(), 'minute'),
      )} ago`
    }

    return (
      <span
        className={
          status !== 'active' ? 'font-medium text-color' : 'text-color-subtle'
        }
      >
        {message}
      </span>
    )
  }, [active, activeDate, expireDate])

  return (
    <Modal
      closeHandler={closeHandler}
      escPressClose
      header={
        <UserAnPCheck
          check={(user) => user.anp.role.ops.permissions.createPassDowns}
        >
          <EditButton onClick={() => onEditClick?.(id)} />
        </UserAnPCheck>
      }
      width="450px"
    >
      {!!primaryImageUrl && (
        <Image src={primaryImageUrl} fluid fit="cover" rounded height={300} />
      )}

      <div className={primaryImageUrl && 'mt-sm'}>{statusMessage}</div>

      <div className="text-lg font-medium capitalize">{title}</div>

      <div className="mt-sm border-t border-border" />

      <div className="mt-sm whitespace-pre-wrap">{body}</div>

      {Array.isArray(passDown.fileUrls) && !!passDown.fileUrls.length && (
        <div className="mt-sm">
          <FilesListDisplay fileUrls={passDown.fileUrls} />
        </div>
      )}
    </Modal>
  )
}

export { PassDownModal }
